import type { ToolUseItem } from '../../harness.types';
import { isRecord } from '../../parse-utils';
import { readToolResultJsonCandidates, readToolResultText } from '../../toolResult';

export { isRecord, readToolResultJsonCandidates };

export const readToolResultRecord = (
  item: ToolUseItem
): Record<string, unknown> | undefined => {
  const content = item.result?.content;
  if (isRecord(content)) return content;

  const text = readToolResultText(item);
  if (!text) return undefined;

  try {
    const parsed: unknown = JSON.parse(text);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const readToolResultMetadata = (
  item: ToolUseItem
): Record<string, unknown> | undefined => {
  const metadata = item.result_provider_metadata ?? item.provider_metadata;
  if (!isRecord(metadata)) return undefined;

  const additionalMetadata = metadata.additional_metadata;
  return isRecord(additionalMetadata) ? additionalMetadata : metadata;
};

export const readToolResultData = (
  item: ToolUseItem
): Record<string, unknown> | undefined => {
  const metadata = readToolResultMetadata(item);
  const data = metadata?.data;
  return isRecord(data) ? data : undefined;
};

export const readString = (
  value: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const raw = value?.[key];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
};

export const readNumber = (
  value: Record<string, unknown> | undefined,
  key: string
): number | undefined => {
  const raw = value?.[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
};

export const readBoolean = (
  value: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  const raw = value?.[key];
  return typeof raw === 'boolean' ? raw : undefined;
};

export const readRecordArray = (
  value: Record<string, unknown> | undefined,
  key: string
): Record<string, unknown>[] | undefined => {
  const raw = value?.[key];
  if (!Array.isArray(raw)) return undefined;
  return raw.every(isRecord) ? raw : undefined;
};
